/**
 * CreditShop — Trang mua credit packs
 * Hiển thị 4 packs + current balance + transaction history
 */

import React, { useState } from 'react';
import { Coins, Star, ArrowLeft, History, ShoppingBag, Check } from 'lucide-react';
import { useCredits } from '../../hooks/useCredits';
import { formatCredits, formatVnd, tokensToPages } from '../../services/mock/creditMock';
import TransactionHistory from './TransactionHistory';
import type { CreditPack } from '../../types/credit';
import { track } from '../../services/analytics';

interface CreditShopProps {
  onBack?: () => void;
  onSelectPack?: (pack: CreditPack) => void;
}

const CreditShop: React.FC<CreditShopProps> = ({ onBack, onSelectPack }) => {
  const { balance, packs, purchasePack } = useCredits();
  const [activeTab, setActiveTab] = useState<'shop' | 'history'>('shop');
  const [purchasedPackId, setPurchasedPackId] = useState<string | null>(null);

  const handleBuy = (pack: CreditPack) => {
    track('credit_purchase_start', {
      pack_id: pack.id,
      pack_name: pack.name,
      price_vnd: pack.priceVnd,
      credits: pack.credits,
    });
    if (onSelectPack) {
      onSelectPack(pack);
    } else {
      // Mock: purchase directly
      purchasePack(pack.id);
      setPurchasedPackId(pack.id);
      track('credit_purchase_success', { pack_id: pack.id, price_vnd: pack.priceVnd });
      setTimeout(() => setPurchasedPackId(null), 2000);
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
          )}
          <ShoppingBag className="w-5 h-5 text-indigo-600" />
          <h2 className="font-bold text-slate-800">Mua Credits</h2>
        </div>

        {balance && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg">
            <Coins className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-700 font-mono">
              {formatCredits(balance.credits)}
            </span>
            <span className="text-xs text-emerald-500">credits</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4 flex gap-2 shrink-0">
        <button
          onClick={() => setActiveTab('shop')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'shop' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-white'
          }`}
        >
          <ShoppingBag className="w-4 h-4 inline mr-1.5" />
          Gói Credits
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'history' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-white'
          }`}
        >
          <History className="w-4 h-4 inline mr-1.5" />
          Lịch sử
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'shop' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`relative bg-white rounded-2xl border-2 p-5 transition-all hover:shadow-lg ${
                  pack.isPopular
                    ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-lg shadow-indigo-200/50'
                    : 'border-slate-200 hover:border-indigo-200'
                }`}
                style={pack.isPopular ? { boxShadow: '0 0 20px rgba(79,70,229,0.2), 0 0 60px rgba(79,70,229,0.08)' } : undefined}
              >
                {/* Popular badge */}
                {pack.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-wider rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Lựa chọn tối ưu
                  </div>
                )}

                {/* Pack info */}
                <div className="mb-4">
                  <h3 className="font-bold text-lg text-slate-800">{pack.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{pack.description}</p>
                </div>

                {/* Credits amount */}
                <div className="mb-1">
                  <span className="text-3xl font-black text-slate-800 font-mono">
                    {formatCredits(pack.totalCredits)}
                  </span>
                  <span className="text-sm text-slate-500 ml-1">credits</span>
                </div>

                {/* Equivalent */}
                <p className="text-xs text-slate-400 mb-4">
                  Dịch được ~{tokensToPages(pack.totalCredits)} trang
                </p>

                {/* Price */}
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-2xl font-black text-indigo-600">
                    {formatVnd(pack.priceVnd)}
                  </span>
                  {pack.savingsPercent > 0 && (
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full mb-1">
                      Tiết kiệm {pack.savingsPercent}%
                    </span>
                  )}
                </div>

                {/* Buy button */}
                <button
                  onClick={() => handleBuy(pack)}
                  disabled={purchasedPackId === pack.id}
                  className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${
                    purchasedPackId === pack.id
                      ? 'bg-emerald-100 text-emerald-700'
                      : pack.isPopular
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100'
                      : 'bg-slate-100 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700'
                  }`}
                >
                  {purchasedPackId === pack.id ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <Check className="w-4 h-4" /> Đã mua!
                    </span>
                  ) : (
                    'Mua ngay'
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <TransactionHistory />
        )}
      </div>
    </div>
  );
};

export default CreditShop;
